import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ProjectHeaderName } from "@/api/ApiClientProvider";
import { useApi } from "@/api/hooks/useApi";
import type {
  DicoStatusDto,
  EditTicketCommentRequest,
  HomeDnaStatusDto,
  HttpResponse,
  ResponsibilitySuggestionRatingRequest,
  StartAppointmentPlanningRequest,
  TicketAssigneeChangeRequest,
  TicketCommentRequest,
  TicketIntegrationStatusSummaryDto,
  TicketRateRequest,
  TicketRequest,
  TicketStatusChangeRequest,
  WunderbricksStatusDto,
} from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { downloadFile } from "@/helpers/file-parse";
import { isHttpError } from "@/helpers/Network/errors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useConfig } from "@/providers/config/useConfig";
import { QUERY_KEYS } from "@/query-keys";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { ApiQueryParams, ApiResponseType } from "@/types/api-types";

function createTicketIntegrationSummary(
  status: { state: string; errorDetails?: string; externalId?: string; caseId?: string },
  current?: TicketIntegrationStatusSummaryDto,
): TicketIntegrationStatusSummaryDto {
  return {
    present: true,
    enabled: current?.enabled ?? true,
    state: status.state,
    externalId: status.externalId ?? status.caseId ?? current?.externalId,
    updatedAt: new Date().toISOString(),
    errorDetails: status.errorDetails,
    data: status,
  };
}

const useAddComment = () => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ ticketId, data }: { ticketId: string; data: TicketCommentRequest; fetchAllActivities?: boolean }) =>
      api.postTicketsCommentsV1(ticketId, data),
    onSuccess: (_, payload) => {
      queryClient.setQueryData<ApiResponseType<"getAdminTicketsDetailsV1">>(
        QUERY_KEYS.TICKETS_DETAILS(projectId, payload.ticketId),
        (oldData) => {
          if (!oldData) {
            return;
          }

          return {
            ...oldData,
            data: {
              ...oldData.data,
              activityCount: (oldData.data.activityCount || 0) + 1,
            },
          };
        },
      );
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.TICKETS_DETAILS_ACTIVITIES(projectId, payload.ticketId, {
          sortDescending: payload.fetchAllActivities,
        }),
      });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_DETAILS(projectId, payload.ticketId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_TICKETS });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_FEED(projectId) });
    },
    onError: (_, payload) => {
      showFlashToast({
        variant: "alert",
        message:
          payload.data.accessType === "internal"
            ? t("page.tickets.details.note.error")
            : t("page.tickets.details.reply.error"),
      });

      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_DETAILS(projectId, payload.ticketId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_TICKETS });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_FEED(projectId) });
    },
  });
};

const useUpdateComment = () => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();

  return useMutation({
    mutationFn: ({
      ticketId,
      commentId,
      data,
    }: {
      ticketId: string;
      commentId: string;
      data: EditTicketCommentRequest;
    }) => api.putTicketsCommentsV1(ticketId, commentId, data),
    onSuccess: (_, payload) => {
      showFlashToast({ variant: "success", message: t("page.tickets.details.edit-note.success") });

      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_DETAILS(projectId, payload.ticketId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_TICKETS });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_FEED(projectId) });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.TICKETS_DETAILS_ACTIVITIES(projectId, payload.ticketId),
      });
    },
    onError: () => {
      showFlashToast({ variant: "success", message: t("page.tickets.details.edit-note.error") });
    },
  });
};

const useUpdateAssignee = () => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();

  return useMutation({
    mutationFn: ({ ticketId, data }: { ticketId: string; data: TicketAssigneeChangeRequest }) =>
      api.putAdminTicketsAssigneeV1(ticketId, data),
    onMutate(payload) {
      // Optimistic update
      queryClient.setQueryData<ApiResponseType<"getAdminTicketsDetailsV1">>(
        QUERY_KEYS.TICKETS_DETAILS(projectId, payload.ticketId),
        (oldData) => {
          if (!oldData) {
            return;
          }

          return {
            ...oldData,
            data: {
              ...oldData.data,
              assignee: oldData.data.possibleAssignees.find((x) => x.id === payload.data.assigneeId)!,
            },
          };
        },
      );
    },
    onSuccess: (_, payload) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_DETAILS(projectId, payload.ticketId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_TICKETS });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_FEED(projectId) });
    },
    onError: (_, payload) => {
      showFlashToast({
        variant: "alert",
        message: t("page.tickets.details.assignee-change.error"),
      });

      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_DETAILS(projectId, payload.ticketId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_TICKETS });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_FEED(projectId) });
    },
  });
};

const useUpdateStatus = () => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();

  return useMutation({
    mutationFn: ({ ticketId, data }: { ticketId: string; data: TicketStatusChangeRequest }) =>
      api.putAdminTicketsStatusV1(ticketId, data),
    onMutate: (payload) => {
      // Optimistic update
      queryClient.setQueryData<ApiResponseType<"getAdminTicketsDetailsV1">>(
        QUERY_KEYS.TICKETS_DETAILS(projectId, payload.ticketId),
        (oldData) => {
          if (!oldData) {
            return;
          }

          return {
            ...oldData,
            data: {
              ...oldData.data,
              status: oldData.data.possibleStatuses.find((x) => x.id === payload.data.statusId)!,
            },
          };
        },
      );
    },
    onSuccess: (_, payload) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_DETAILS(projectId, payload.ticketId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_TICKETS });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_FEED(projectId) });
    },
    onError: (_, payload) => {
      showFlashToast({
        variant: "alert",
        message: t("page.tickets.details.status-change.error"),
      });

      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_DETAILS(projectId, payload.ticketId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_TICKETS });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_FEED(projectId) });
    },
  });
};

const useUpdateResponsibilitySuggestion = (ticketId: string) => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ResponsibilitySuggestionRatingRequest) =>
      api.postAdminTicketsResponsibilitySuggestionV1(ticketId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.TICKETS_RESPONSIBILITY_SUGGESTION(projectId, ticketId),
      });
    },
  });
};

const useGetTicketCopilotSuggestion = (ticketId: string) => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: { additionalInfo?: string }) =>
      api.postTicketsSuggestionV1(ticketId, data).then((res) => res.data),
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.tickets.details.copilot.generate.error") });
    },
  });
};

const useSendTicketToHomeDna = (ticketId: string) => {
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const api = useApi();

  function overwriteHomeDna(response: Pick<HttpResponse<HomeDnaStatusDto>, "data">) {
    queryClient.setQueryData<ApiResponseType<"getAdminTicketsDetailsV1">>(
      QUERY_KEYS.TICKETS_DETAILS(projectId, ticketId),
      (oldData) => {
        if (!oldData) {
          return;
        }

        return {
          ...oldData,
          data: {
            ...oldData.data,
            integrations: {
              ...oldData.data.integrations,
              homeDna: createTicketIntegrationSummary(response.data, oldData.data.integrations?.homeDna),
            },
          },
        };
      },
    );
  }

  return useMutation({
    mutationFn: () => api.postAdminTicketsHomednaV1(ticketId),
    onSuccess(response) {
      overwriteHomeDna(response);
    },
    onError(error) {
      const errorDetails =
        isHttpError(error) &&
        error.error &&
        typeof error.error === "object" &&
        "errorDetails" in error.error &&
        typeof error.error.errorDetails === "string"
          ? error.error.errorDetails
          : undefined;

      overwriteHomeDna({ data: { state: "failed", errorDetails } });
    },
  });
};

const useSendTicketToDico = (ticketId: string) => {
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const api = useApi();

  function overwriteDico(response: Pick<HttpResponse<DicoStatusDto>, "data">) {
    queryClient.setQueryData<ApiResponseType<"getAdminTicketsDetailsV1">>(
      QUERY_KEYS.TICKETS_DETAILS(projectId, ticketId),
      (oldData) => {
        if (!oldData) {
          return;
        }

        return {
          ...oldData,
          data: {
            ...oldData.data,
            integrations: {
              ...oldData.data.integrations,
              dico: createTicketIntegrationSummary(response.data, oldData.data.integrations?.dico),
            },
          },
        };
      },
    );
  }

  return useMutation({
    mutationFn: () => api.postAdminTicketsDicoV1(ticketId),
    onSuccess(response) {
      overwriteDico(response);
    },
    onError(error) {
      const errorDetails =
        isHttpError(error) &&
        error.error &&
        typeof error.error === "object" &&
        "errorDetails" in error.error &&
        typeof error.error.errorDetails === "string"
          ? error.error.errorDetails
          : undefined;

      overwriteDico({ data: { state: "failed", errorDetails } });
    },
  });
};

const useSendTicketToWunderbricks = (ticketId: string) => {
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const api = useApi();

  function overwriteWunderbricks(response: Pick<HttpResponse<WunderbricksStatusDto>, "data">) {
    queryClient.setQueryData<ApiResponseType<"getAdminTicketsDetailsV1">>(
      QUERY_KEYS.TICKETS_DETAILS(projectId, ticketId),
      (oldData) => {
        if (!oldData) {
          return;
        }

        return {
          ...oldData,
          data: {
            ...oldData.data,
            integrations: {
              ...oldData.data.integrations,
              wunderbricks: createTicketIntegrationSummary(response.data, oldData.data.integrations?.wunderbricks),
            },
          },
        };
      },
    );
  }

  return useMutation({
    mutationFn: () => api.postAdminTicketsWunderbricksV1(ticketId),
    onSuccess(response) {
      overwriteWunderbricks(response);
    },
    onError(error) {
      const errorDetails =
        isHttpError(error) &&
        error.error &&
        typeof error.error === "object" &&
        "errorDetails" in error.error &&
        typeof error.error.errorDetails === "string"
          ? error.error.errorDetails
          : undefined;

      overwriteWunderbricks({ data: { state: "failed", isClosed: false, canSubmit: true, errorDetails } });
    },
  });
};

const useSelectAppointment = (ticketId: string) => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (slotId: string) => api.postTicketsAppointmentSelectV1(ticketId, { slotId }),
    onSuccess: (response) => {
      // The call can succeed while the slot was taken in the meantime: the ticket then does not
      // reach `confirmed`. Surface that as "moment no longer available" and let the resident re-pick
      // from the refreshed options.
      if (response.data.state !== "confirmed" && response.data.state !== "appointmentChanged") {
        showFlashToast({
          variant: "alert",
          message: t("page.tickets.appointment.select-appointment.not-available"),
        });
      }

      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.TICKETS_DETAILS_APPOINTMENT(projectId, ticketId),
      });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.tickets.appointment.select-appointment.error") });
    },
  });
};

const useStartAppointmentPlanning = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: StartAppointmentPlanningRequest) =>
      api.postTicketsAppointmentPlanningsV1(data).then((response) => response.data),
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.resident-tickets.create-modal.appointment.error") });
    },
  });
};

const useChangeAppointment = (ticketId: string) => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: () => api.postTicketsAppointmentChangeV1(ticketId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.TICKETS_DETAILS_APPOINTMENT(projectId, ticketId),
      });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.tickets.appointment.change-appointment.error") });
    },
  });
};

const useUpdateRating = (ticketId: string) => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: TicketRateRequest) =>
      api.putTicketsRateV1(ticketId, {
        remark: payload.remark,
        rating: payload.rating ?? 0,
        closedAt: payload.closedAt,
        feedbackOptions: payload.feedbackOptions,
      }),
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_RESIDENT_DETAILS(projectId, ticketId) });
    },
    async onError() {
      showFlashToast({
        variant: "alert",
        message: t("page.tickets.details.rate.error"),
      });

      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_RESIDENT_DETAILS(projectId, ticketId) });
    },
  });
};

const useGetQuickReplyLink = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const dashboardUrl = useConfig("newDashboardRootUrl");

  return useMutation({
    mutationFn: (ticketId: string) => api.postQuickReplyTicketCreateTokenV2({ ticketId }).then((x) => x.data),
    onSuccess: async (token) => {
      const quickReplyMessagePath = routes.quickReply.ticket({ token: `V2-${token}` });

      await navigator.clipboard.writeText(`${dashboardUrl}${quickReplyMessagePath}`);

      showFlashToast({ variant: "success", message: t("page.tickets.copy-quick-reply-link.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.tickets.copy-quick-reply-link.error") });
    },
  });
};

const useAddTicket = () => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: TicketRequest) => api.postTicketsV1(data),
    onSuccess: (response) => {
      showFlashToast({
        variant: "success",
        message: t("page.tickets.create.action.create.notification.success"),
      });

      return response.data;
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.tickets.create.action.create.notification.error"),
        "data-testid": "request-error",
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS(projectId) });
    },
  });
};

const useMarkTicketAsRead = (ticketId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: () => api.postTicketsReadV1(ticketId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_FEED(projectId) }),
  });
};

const useDeleteTicket = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const loggedInProjectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ ticketId, projectId }: { ticketId: string; projectId?: string }) =>
      api.deleteAdminTicketsByIdV1(ticketId, {
        headers: {
          [ProjectHeaderName]: projectId || loggedInProjectId,
        },
      }),
    onSuccess: (_, payload) => {
      showFlashToast({ variant: "success", message: t("page.tickets.delete.success") });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_FEED(payload.projectId || loggedInProjectId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_TICKETS });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.tickets.delete.error") });
    },
  });
};

const useMarkAllAsRead = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (query: ApiQueryParams<"postAdminTicketsReadV1">) => api.postAdminTicketsReadV1(query),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("page.tickets.mark-all-as-read.success") });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_FEED(projectId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_TICKETS });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.tickets.mark-all-as-read.error") });
    },
  });
};

const useExportTickets = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: { query: ApiQueryParams<"getAdminTicketsExportV1">; filename: string }) =>
      api.getAdminTicketsExportV1(payload.query),
    onSuccess: async (data, payload) => {
      const blob = await data.blob();
      downloadFile(blob, payload.filename);
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.tickets.export.error") });
    },
  });
};

const useResidentReply = (ticketId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: TicketCommentRequest) => api.postTicketsCommentsV1(ticketId, payload),
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_RESIDENT_DETAILS(projectId, ticketId) });
    },
    async onError() {
      showFlashToast({ variant: "alert", message: t("page.tickets.details.reply.error") });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_RESIDENT_DETAILS(projectId, ticketId) });
    },
  });
};

const useCreateReminder = (ticketId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: { reminderDate: string }) => api.postAdminTicketsReminderV1(ticketId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_DETAILS(projectId, ticketId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_FEED(projectId), refetchType: "inactive" });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO });
      showFlashToast({ variant: "success", message: t("page.tickets.details.reminder.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.tickets.details.reminder.error") });
    },
  });
};

const useDeleteReminder = (ticketId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: () => api.deleteAdminTicketsReminderDeleteV1(ticketId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_DETAILS(projectId, ticketId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TICKETS_FEED(projectId), refetchType: "inactive" });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO });
    },
  });
};

const useUploadTicketToRems = (ticketId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: (payload: Parameters<typeof api.postAdminTicketsRemsSendTicketV1>[1]) =>
      api.postAdminTicketsRemsSendTicketV1(ticketId, payload),
    onSuccess(response) {
      queryClient.setQueryData<ApiResponseType<"getAdminTicketsDetailsV1">>(
        QUERY_KEYS.TICKETS_DETAILS(projectId, ticketId),
        (oldData) => {
          if (!oldData) return;

          return {
            ...oldData,
            data: {
              ...oldData.data,
              integrations: {
                ...oldData.data.integrations,
                rems: createTicketIntegrationSummary(response.data, oldData.data.integrations?.rems),
              },
            },
          };
        },
      );
    },
  });
};

const useSyncTicketRems = (ticketId: string) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: () => api.postAdminTicketsRemsSyncV1(ticketId),
    onSuccess(response) {
      queryClient.setQueryData<ApiResponseType<"getAdminTicketsDetailsV1">>(
        QUERY_KEYS.TICKETS_DETAILS(projectId, ticketId),
        (oldData) => {
          if (!oldData) return;

          return {
            ...oldData,
            data: {
              ...oldData.data,
              integrations: {
                ...oldData.data.integrations,
                rems: createTicketIntegrationSummary(response.data, oldData.data.integrations?.rems),
              },
            },
          };
        },
      );
    },
  });
};

export const ticketMutations = {
  useAddComment,
  useUpdateComment,
  useUpdateAssignee,
  useUpdateStatus,
  useUpdateRating,
  useUpdateResponsibilitySuggestion,
  useGetTicketCopilotSuggestion,
  useSendTicketToHomeDna,
  useSendTicketToDico,
  useSendTicketToWunderbricks,
  useGetQuickReplyLink,
  useAddTicket,
  useMarkTicketAsRead,
  useDeleteTicket,
  useMarkAllAsRead,
  useExportTickets,
  useResidentReply,
  useCreateReminder,
  useDeleteReminder,
  useUploadTicketToRems,
  useSyncTicketRems,
  useSelectAppointment,
  useChangeAppointment,
  useStartAppointmentPlanning,
};
